// Google Analytics types
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Google Analytics Configuration
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('🔍 Google Analytics not configured. Add REACT_APP_GA_MEASUREMENT_ID to your .env file.');
    console.info('📖 See SETUP_GUIDE.md for detailed setup instructions.');
    return;
  }
  
  console.log('📊 Initializing Google Analytics with ID:', GA_MEASUREMENT_ID);

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
  });

  // Make gtag globally available
  (window as any).gtag = gtag;
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
    });
  }
};

// Track events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track affiliate link clicks and store in Firebase
export const trackAffiliateClick = async (linkUrl: string, linkText: string) => {
  // Track with Google Analytics
  trackEvent('click', 'affiliate', linkUrl, 1);
  trackEvent('affiliate_click', 'engagement', linkText);
  
  // Store real click data in Firebase
  try {
    const { collection, addDoc, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    if (db) {
      await addDoc(collection(db, 'buyButtonClicks'), {
        linkUrl,
        linkText,
        clickedAt: Timestamp.now(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct'
      });
      console.log('✅ Buy button click tracked in Firebase');
    }
  } catch (error) {
    console.error('❌ Error tracking buy button click:', error);
  }
};

// Track comment submissions
export const trackCommentSubmission = (postId: string) => {
  trackEvent('submit_comment', 'engagement', postId);
};

// Track post views and store in Firebase
export const trackPostView = async (postId: string, postTitle: string) => {
  // Track with Google Analytics
  trackEvent('view', 'post', postTitle, 1);
  trackEvent('post_view', 'engagement', postId);
  
  // Store real page view in Firebase
  try {
    const { collection, addDoc, doc, updateDoc, increment, getDoc, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    if (db) {
      // Store individual page view record
      await addDoc(collection(db, 'pageViews'), {
        postId,
        postTitle,
        viewedAt: Timestamp.now(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        sessionId: generateSessionId()
      });
      
      // Update post's view count
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (postDoc.exists()) {
        await updateDoc(postRef, {
          views: increment(1),
          lastViewedAt: Timestamp.now()
        });
        console.log(`✅ Page view tracked for post: ${postTitle}`);
      }
    }
  } catch (error) {
    console.error('❌ Error tracking page view:', error);
  }
};

// Generate a simple session ID for tracking unique sessions
const generateSessionId = (): string => {
  const sessionKey = 'nb_session_id';
  let sessionId = sessionStorage.getItem(sessionKey);
  
  if (!sessionId) {
    sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    sessionStorage.setItem(sessionKey, sessionId);
  }
  
  return sessionId;
};

// Helper function to get icons for traffic sources
const getSourceIcon = (source: string): string => {
  switch (source) {
    case 'Google Search': return '🔍';
    case 'Facebook': return '📘';
    case 'Twitter': return '🐦';
    case 'LinkedIn': return '💼';
    case 'Direct': return '🔗';
    case 'Other Websites': return '🌐';
    default: return '📊';
  }
};

// Analytics Data Interface
export interface AnalyticsData {
  dailyVisits: { day: string; visits: number }[];
  monthlyViewers: { total: number; growth: number };
  topLocations: { country: string; flag: string; percentage: number }[];
  buyButtonStats: {
    totalClicks: number;
    monthlyClicks: number;
    weeklyClicks: number;
    topCategory: {
      name: string;
      clicks: number;
    };
  };
  realPageViews?: {
    totalViews: number;
  };
}

// Mock data for development (replace with real GA4 API calls)
export const getAnalyticsData = async (): Promise<AnalyticsData> => {
  // In production, this would make calls to Google Analytics Reporting API
  // For now, returning enhanced mock data with some randomization
  
  const today = new Date();
  const dailyVisits = [];
  
  // Generate last 7 days of data with some variation
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const baseVisits = 45 + Math.floor(Math.random() * 40); // 45-85 visits
    
    dailyVisits.push({
      day: dayName,
      visits: baseVisits
    });
  }

  // Calculate monthly viewers with realistic growth
  const monthlyTotal = dailyVisits.reduce((sum, day) => sum + day.visits, 0) * 4.3; // Approximate monthly
  const growth = Math.floor(Math.random() * 30) + 10; // 10-40% growth

  return {
    dailyVisits,
    monthlyViewers: {
      total: Math.floor(monthlyTotal),
      growth
    },
    topLocations: [
      { country: 'United States', flag: '🇺🇸', percentage: 42 },
      { country: 'Canada', flag: '🇨🇦', percentage: 18 },
      { country: 'United Kingdom', flag: '🇬🇧', percentage: 15 },
      { country: 'Australia', flag: '🇦🇺', percentage: 12 },
      { country: 'Others', flag: '🌍', percentage: 13 }
    ],
    buyButtonStats: {
      totalClicks: Math.floor(Math.random() * 50) + 10,
      monthlyClicks: Math.floor(Math.random() * 30) + 5,
      weeklyClicks: Math.floor(Math.random() * 10) + 2,
      topCategory: {
        name: 'Technology',
        clicks: Math.floor(Math.random() * 20) + 5
      }
    }
  };
};

// Real Analytics Data from Firebase (Browser-Compatible)
export const getRealAnalyticsData = async (): Promise<AnalyticsData | null> => {
  try {
    console.log('📊 Calculating real analytics data from Firebase...');
    
    // Import Firebase functions
    const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    if (!db) {
      console.log('🔧 Firebase not configured - using mock data');
      return null;
    }

    // Get date ranges
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const fifteenDaysAgo = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));
    
    // Fetch all posts to analyze publish dates and views
    const postsQuery = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'));
    const postsSnapshot = await getDocs(postsQuery);
    const posts = postsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      publishedAt: doc.data().publishedAt?.toDate() || new Date(),
      views: doc.data().views || Math.floor(Math.random() * 500) + 50 // Use real views or estimate
    }));

    // Fetch users to get user count growth
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));

    // Fetch comments for engagement metrics
    const commentsQuery = query(collection(db, 'comments'));
    const commentsSnapshot = await getDocs(commentsQuery);
    const comments = commentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));

    // Will calculate daily visits after fetching pageViews data

    // Calculate total users and growth
    const recentUsers = users.filter(user => user.createdAt >= fifteenDaysAgo).length;
    const olderUsers = users.filter(user => user.createdAt < fifteenDaysAgo && user.createdAt >= thirtyDaysAgo).length;
    const growth = olderUsers > 0 ? Math.round(((recentUsers - olderUsers) / olderUsers) * 100) : recentUsers > 0 ? 100 : 0;

    // Will calculate top traffic sources after fetching pageViews data

    // Fetch real Buy button clicks from Firebase
    const clicksQuery = query(collection(db, 'buyButtonClicks'));
    const clicksSnapshot = await getDocs(clicksQuery);
    const totalBuyButtonClicks = clicksSnapshot.docs.length;
    
    // Calculate monthly and weekly clicks
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const weekStart = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    
    const monthlyClicks = clicksSnapshot.docs.filter(doc => {
      const clickData = doc.data();
      const clickDate = clickData.clickedAt?.toDate();
      return clickDate && clickDate >= monthStart;
    }).length;
    
    const weeklyClicks = clicksSnapshot.docs.filter(doc => {
      const clickData = doc.data();
      const clickDate = clickData.clickedAt?.toDate();
      return clickDate && clickDate >= weekStart;
    }).length;
    
    // Calculate top category for buy button clicks
    const categoryClickCounts = new Map<string, number>();
    
    // Count clicks by category (need to match postId to post category)
    for (const clickDoc of clicksSnapshot.docs) {
      const clickData = clickDoc.data();
      const postId = clickData.postId;
      
      // Find the post to get its category
      const post = posts.find(p => p.id === postId);
      const postData = post as any; // Type assertion to access all fields
      if (postData && postData.category) {
        const currentCount = categoryClickCounts.get(postData.category) || 0;
        categoryClickCounts.set(postData.category, currentCount + 1);
      }
    }
    
    // Find the top category
    let topCategory = { name: 'No Data', clicks: 0 };
    if (categoryClickCounts.size > 0) {
      const sortedCategories = Array.from(categoryClickCounts.entries())
        .sort(([,a], [,b]) => b - a);
      const [categoryName, clicks] = sortedCategories[0];
      topCategory = { name: categoryName, clicks };
    }
    
    // Fetch real page views from Firebase
    const pageViewsQuery = query(collection(db, 'pageViews'));
    const pageViewsSnapshot = await getDocs(pageViewsQuery);
    const totalRealPageViews = pageViewsSnapshot.docs.length;
    
    // Calculate real daily visits from Firebase pageViews collection
    const dailyVisits = [];
    for (let i = 6; i >= 0; i--) { // Last 7 days only
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
      const dateStr = date.toISOString().split('T')[0];
      
      // Count real page views for this specific day
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
      
      const dayViews = pageViewsSnapshot.docs.filter(doc => {
        const viewData = doc.data();
        const viewDate = viewData.viewedAt?.toDate();
        return viewDate && viewDate >= dayStart && viewDate < dayEnd;
      }).length;
      
      dailyVisits.push({
        day: dateStr,
        visits: dayViews
      });
    }
    
    // Calculate top traffic sources from real referrer data
    const referrerCounts = new Map<string, number>();
    pageViewsSnapshot.docs.forEach(doc => {
      const viewData = doc.data();
      const referrer = viewData.referrer || 'direct';
      
      // Categorize referrers into meaningful sources
      let source = 'Direct';
      if (referrer.includes('google')) source = 'Google Search';
      else if (referrer.includes('facebook')) source = 'Facebook';
      else if (referrer.includes('twitter')) source = 'Twitter';
      else if (referrer.includes('linkedin')) source = 'LinkedIn';
      else if (referrer !== 'direct' && referrer.includes('http')) source = 'Other Websites';
      
      referrerCounts.set(source, (referrerCounts.get(source) || 0) + 1);
    });
    
    // Convert to top locations format (showing traffic sources instead of countries)
    const totalViewsForSources = pageViewsSnapshot.docs.length;
    const topLocations = Array.from(referrerCounts.entries())
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([source, count]) => ({
        country: source,
        flag: getSourceIcon(source),
        percentage: totalViewsForSources > 0 ? Math.round((count / totalViewsForSources) * 100) : 0
      }));
    
    // Add empty entries if we have less than 5 sources
    while (topLocations.length < 5) {
      topLocations.push({
        country: 'No Data',
        flag: '📊',
        percentage: 0
      });
    }
    
    console.log(`🛒 Real Buy button clicks found: ${totalBuyButtonClicks}`);
    console.log(`👁️ Real page views found: ${totalRealPageViews}`);
    console.log(`📅 Real daily visits calculated:`, dailyVisits);
    console.log(`🌐 Real traffic sources calculated:`, topLocations);

    const analyticsData = {
      dailyVisits,
      monthlyViewers: {
        total: users.length,
        growth
      },
      topLocations,
      buyButtonStats: {
        totalClicks: totalBuyButtonClicks,
        monthlyClicks,
        weeklyClicks,
        topCategory
      },
      realPageViews: {
        totalViews: totalRealPageViews
      }
    };

    console.log('✅ Successfully calculated real analytics from Firebase data');
    console.log(`📈 Total users: ${users.length}, Posts: ${posts.length}, Comments: ${comments.length}`);
    console.log(`📊 Buy button clicks: ${totalBuyButtonClicks}, Growth: ${growth}%`);
    
    return analyticsData;
  } catch (error) {
    console.error('❌ Error calculating real analytics data:', error);
    console.log('🔄 Falling back to mock data');
    return null; // Fall back to mock data
  }
};

// Utility to check if GA is loaded
export const isGALoaded = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).gtag;
};

// Enhanced tracking for blog-specific events
export const trackBlogEvent = {
  searchPerformed: (query: string) => trackEvent('search', 'blog', query),
  categoryViewed: (category: string) => trackEvent('view_category', 'navigation', category),
  newsletterSignup: () => trackEvent('newsletter_signup', 'conversion'),
  socialShare: (platform: string, postTitle: string) => trackEvent('share', 'social', `${platform}:${postTitle}`),
  downloadResource: (resourceName: string) => trackEvent('download', 'resource', resourceName),
};
